import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import type { FamilyMember, Profile } from "../data/profile";
import { FieldList, ProfileParagraph } from "../profilePrimitives";
import { listMissingLabels } from "../profileUtils";
import { FormShell } from "./ProfileOverlayShell";

export function FamilyView({
    member,
    profile,
    onClose,
    onEdit,
    onDelete,
}: {
    member: FamilyMember;
    profile: Profile;
    onClose: () => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    return (
        <FormShell
            title={member.name}
            subtitle={`Linked to ${profile.memberName}`}
            showBack
            onClose={onClose}
            footer={
                <>
                    <Button type="button" variant="destructive" onClick={onDelete}>
                        Delete
                    </Button>
                    <Button type="button" onClick={onEdit}>
                        Edit
                    </Button>
                </>
            }
        >
            {member.mustKnow && <ProfileParagraph>{member.mustKnow}</ProfileParagraph>}
            <FieldList
                rows={familyViewRows(member)}
                missing={familyViewMissing(member)}
            />
        </FormShell>
    );
}

function familyViewRows(member: FamilyMember) {
    return [
        { label: "Relationship", value: member.detail },
        member.dateOfBirth
            ? {
                  label: "Date of birth",
                  value: format(parseISO(member.dateOfBirth), "d MMM yyyy"),
              }
            : undefined,
        member.passportName
            ? { label: "Passport name", value: member.passportName }
            : undefined,
        member.passportNumber
            ? { label: "Passport number", value: member.passportNumber }
            : undefined,
        member.passportExpiryDate
            ? {
                  label: "Passport expires",
                  value: format(parseISO(member.passportExpiryDate), "d MMM yyyy"),
              }
            : undefined,
        member.nationality
            ? { label: "Nationality", value: member.nationality }
            : undefined,
        member.frequentFlyerNumber
            ? { label: "Frequent flyer number", value: member.frequentFlyerNumber }
            : undefined,
    ].filter((row): row is { label: string; value: string } => !!row);
}

function familyViewMissing(member: FamilyMember) {
    if (member.kind !== "limited") return undefined;
    return listMissingLabels([
        { label: "Must know", present: !!member.mustKnow },
        { label: "Date of birth", present: !!member.dateOfBirth },
        { label: "Nationality", present: !!member.nationality },
        { label: "Passport name", present: !!member.passportName },
        { label: "Passport number", present: !!member.passportNumber },
        { label: "Passport expires", present: !!member.passportExpiryDate },
        { label: "Frequent flyer number", present: !!member.frequentFlyerNumber },
    ]);
}
